import { ref } from 'vue';

import { ProductVariant } from '@/__generated__/storefront/graphql';
import { variantPointsPriceQuery } from '@/shared/graphql/storefront/queries/loyalty.queries';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useUserStore } from '@/shared/pinia/modules/user';
import { getApolloClients } from '@/shared/utils/apollo-client';
import { shopifyIdToGid } from '@/shared/utils/shopify/id-formatter';

export type PointPrice = {
  id: string | number;
  pointsPrice: number;
};

export function useCartValidations() {
  const fetchedPointPrices = ref<PointPrice[]>([]);

  const getPointPriceForItemSync = (item: ShopifyCartItem): number => {
    const pointsPriceFromProperties = Number(item.properties?._pointsPrice);
    if (!isNaN(pointsPriceFromProperties)) {
      return pointsPriceFromProperties;
    }
    const fetched = fetchedPointPrices.value.find((p) => p.id === item.id);
    return fetched?.pointsPrice ?? 0;
  };

  const currentPointsInCart = (cartItems: ShopifyCartItem[]) => {
    return cartItems.reduce((pointsInCart, item) => {
      const pointsPerUnit = getPointPriceForItemSync(item);
      if (pointsPerUnit > 0) {
        return pointsInCart + pointsPerUnit * item.quantity;
      }
      return pointsInCart;
    }, 0);
  };

  const validateUserIsAuthenticated = () => {
    const userStore = useUserStore();

    if (!userStore.authenticated) {
      throw new Error(
        window.Hobbii?.cart.messages.notAuthenticatedForUsingPoints ||
          'This item can only be purchased with points as a Hobbii member.'
      );
    }
  };

  const isFromPointshop = (
    item: ShopifyCartItem,
    isAddingMainProduct: boolean = false
  ): boolean => {
    if (isAddingMainProduct) {
      const productStore = useProductStore();
      return productStore.isPointProduct;
    } else {
      const hasPointsProperty = !!item.properties?._pointsPrice || false;

      // if the item is free and it is not a pattern, then it is a point product
      const isFreeAndNotPattern =
        item.final_line_price === 0 &&
        !!item.sku &&
        !item.sku.startsWith('pattern-');

      return hasPointsProperty || isFreeAndNotPattern;
    }
  };

  const getPointsUnitPrice = async (
    item: ShopifyCartItem,
    isAddingMainProduct?: boolean
  ): Promise<number> => {
    if (isAddingMainProduct) {
      const productStore = useProductStore();
      return productStore.pointsPrice || 0;
    } else {
      const fetchedPointsPrice = fetchedPointPrices.value.find(
        (p) => p.id === item.id
      );

      const pointsPriceFromProperties = Number(item.properties?._pointsPrice);
      if (!isNaN(pointsPriceFromProperties)) {
        return pointsPriceFromProperties;
      } else if (fetchedPointsPrice) {
        return fetchedPointsPrice.pointsPrice;
      } else {
        const pointsPricePerUnit = await fetchPointsPriceForItem(item);
        return pointsPricePerUnit;
      }
    }
  };

  const validateUserHasEnoughPoints = async (
    futureTotalPointsInCart: number
  ) => {
    const userStore = useUserStore();
    const customerPointsBalance = await userStore.getCustomerPointsBalance();

    if (
      futureTotalPointsInCart > 0 &&
      customerPointsBalance != null &&
      customerPointsBalance < futureTotalPointsInCart
    ) {
      throw new Error(
        window.Hobbii?.cart.messages.insufficientPoints ||
          'Almost there! You need to earn some points to be able to buy this item'
      );
    }
  };

  const fetchPointsPriceForItem = async (
    item: ShopifyCartItem
  ): Promise<number> => {
    const { storefront } = await getApolloClients();
    const pointsPriceQueryResponse = await storefront.query({
      query: variantPointsPriceQuery,
      variables: {
        id: shopifyIdToGid(item.id.toString(), 'ProductVariant')
      }
    });
    const productVariant = pointsPriceQueryResponse.data.node as ProductVariant;

    const pointsPrice = Number(productVariant?.metafield?.value) || 0;
    if (pointsPrice > 0) {
      fetchedPointPrices.value.push({ id: item.id, pointsPrice });
    }

    return pointsPrice;
  };

  const checkAndFetchPointsPricesWhenNeeded = async (
    cartItems: ShopifyCartItem[]
  ) => {
    for (const item of cartItems) {
      const isPointProduct = isFromPointshop(item);

      if (isPointProduct) {
        const alreadyCalculated = fetchedPointPrices.value.find(
          (p) => p.id === item.id
        );

        if (!alreadyCalculated) {
          await fetchPointsPriceForItem(item);
        }
      }
    }
  };

  /**
   * Validates that the points products, if exist, can be added to the cart
   * @param payload
   * @returns
   */
  const validateAddPayload = async (
    payload: ShopifyCartAddPayload,
    cartItems: ShopifyCartItem[]
  ) => {
    const isThereAPointProduct = payload.items.some((item) =>
      isFromPointshop(item, true)
    );

    if (isThereAPointProduct) {
      validateUserIsAuthenticated();

      //calculate future points in cart
      const pointPricesToBeAdded: number[] = await Promise.all(
        payload.items.map(async (item: ShopifyCartItem) => {
          const pointsUnitPrice = await getPointsUnitPrice(item, true);

          return pointsUnitPrice * item.quantity;
        })
      );
      const pricesToAdd = pointPricesToBeAdded.reduce(
        (acc, price) => acc + price
      );

      const futurePointsInCart = currentPointsInCart(cartItems) + pricesToAdd;

      await validateUserHasEnoughPoints(futurePointsInCart);
    }

    return true;
  };

  /**
   * validate that the points products, if exist, can be changed in the cart
   */
  const validateChangePayload = async (
    payload: {
      line: number;
      quantity: number;
    },
    cartItems: ShopifyCartItem[]
  ) => {
    // if deleting, no validation is needed
    if (payload.quantity > 0) {
      const itemInCart = cartItems?.[payload.line - 1];

      if (itemInCart && isFromPointshop(itemInCart)) {
        validateUserIsAuthenticated();

        //calculate future points in cart
        const itemCurrentPointsPrice = await getPointsUnitPrice(itemInCart);
        const itemCurrentPointsInCart =
          itemCurrentPointsPrice * itemInCart.quantity;
        const itemFuturePointsInCart =
          itemCurrentPointsPrice * payload.quantity;

        const futureTotalPointsInCart =
          currentPointsInCart(cartItems) +
          itemFuturePointsInCart -
          itemCurrentPointsInCart;

        await validateUserHasEnoughPoints(futureTotalPointsInCart);
      }
    }
  };

  return {
    validateAddPayload,
    validateChangePayload,
    checkAndFetchPointsPricesWhenNeeded,
    fetchedPointPrices,
    getPointPriceForItemSync,
    isFromPointshop
  };
}
